/**
 * How good the enquiry was, and whether we came away able to act on it.
 *
 * ONE PRIMITIVE, TWO READINGS OF IT. `Steps` is "n of m lit", and both call sites use it:
 * the list draws the SCORE as quarters, the detail page draws each MEASURE as its level.
 * Two components would drift into two bar heights and two radii within a month, and a
 * table whose bars are a different object from the panel's bars is a table nobody connects
 * to the panel.
 *
 * A SCORED CALL ALWAYS LIGHTS AT LEAST ONE STEP, so "cold, and we established that" can
 * never be confused with "nothing was drawn here". A call nobody has read for a lead gets
 * no bar at all and the words "Not scored" - absence drawn as absence.
 *
 * `unknown` IS A DASHED OUTLINE, NEVER AN EMPTY FILL. "Nobody established the timing" and
 * "they told us and it was weak" are different facts, and a grey segment says the second.
 * It is the only dashed border in the product, for exactly that reason.
 *
 * COLOUR COMES FROM THE EXISTING HEAT RAMP AND THERE IS NO NEW TOKEN. `styles.css` is explicit
 * that the palette is validated output rather than preference - six checks including
 * deuteranopia and tritanopia separation - and THAT VALIDATOR IS NOT IN THIS REPO, so a
 * new four-hue set could not be checked here. One hue light-to-dark is also the correct
 * encoding for an ORDERED quantity; the four `band-*` colours are explicitly categorical
 * ("never cycled, never assigned by rank"), so cycling them here would paint an ordinal
 * scale in four unrelated hues.
 *
 * If somebody later wants a distinct tint for `cool`, the honest route is a
 * `--color-cool`/`--color-cool-soft` pair AND adding "cool" to `BADGES` in
 * scripts/check-contrast.mjs - the guard only covers what it is told about, and a token
 * that silently escapes it is the failure the guard exists to prevent. Fills are exempt
 * from that script by its own header, so the build will not stop you, which is precisely
 * why it has to be deliberate.
 *
 * THE BAND WORD IS INK, NOT A TINT, AND HOT IS NOT GREEN. `--color-good` means an outcome
 * that HAPPENED - booked, qualified, handled. A hot lead is a promise, and painting it the
 * same green as "Booked" puts a thing somebody still has to do in the same visual class as
 * a thing that is done.
 */

import type { ReactNode } from "react";
import type { Band, Measure, Qualification } from "../api";
import { ScoreRing } from "../charts";
import { Check, Clock, Phone, Sparkle, Stack } from "./Icons";
import { cx } from "../ui";

/** Fixed platform vocabulary, as `sentiment` is. See `bandWord` for the business's override. */
export const BAND_WORDS: Record<Band, string> = {
  hot: "Hot",
  warm: "Warm",
  cool: "Cool",
  cold: "Cold",
  unscorable: "Not established",
  not_a_fit: "Not a fit",
  not_an_enquiry: "Not an enquiry",
};

/**
 * The business's own word for a band, if they set one.
 *
 * Under DISTINCT KEYS - `lead_hot`, not `hot` - so they cannot collide with an agent that
 * already sends `hot` and `warm` as outcome LABELS. `config/ecoboats.json` does exactly
 * that: its `labels.hot` is "Hot lead", which is the grade its own agent wrote and a
 * different fact from this one.
 */
export function bandWord(labels: Record<string, string>, band: Band): string {
  return labels[`lead_${band}`] ?? BAND_WORDS[band];
}

const CAPTURE_WORDS: Record<string, string> = {
  good: "we can follow this up",
  partial: "only half a way back to them",
  none: "no way to reach them",
};

/**
 * What a lit segment is filled with, indexed by how many of the four are lit.
 *
 * IT STOPS AT THE ACCENT AND NEVER REACHES INK, and that is a correction rather than a
 * preference. This used to walk the whole heat ramp - `heat-1` through `heat-4` - so a
 * four-lit meter was filled with `#111014`, and the hottest lead in the log was drawn in
 * the one colour this product uses for TEXT. Beside a violet score ring and the word "Hot"
 * set in `text-accent`, the same call was rendering its own grade in two colours.
 *
 * THE COUNT IS THE ORDINAL CHANNEL HERE, WHICH IS WHY THE FILL DOES NOT HAVE TO BE. The
 * heatmap needs the full ramp because one cell has nothing else to say how busy it is; a
 * meter has four segments and lights n of them, so a fill that also darkens step by step is
 * a second encoding of a variable already encoded. What the colour is free to carry instead
 * is the thing the count cannot: whether this is worth acting on. Grey below warm, accent at
 * warm and above - which is the cut the band words already make, and what the design this
 * page was built from draws.
 *
 * The note this replaces argued that one hue light-to-dark is the correct encoding for an
 * ORDERED quantity, and that the four categorical `band-*` colours must never be cycled by
 * rank. Both are still true and neither is contradicted: this is one hue at two steps, and
 * no band colour appears. `--color-heat-4` is untouched and the heatmap still uses it.
 */
const HEAT = ["bg-heat-2", "bg-heat-2", "bg-heat-3", "bg-heat-3"];

const LEVEL_STEPS: Record<string, number> = { none: 1, low: 2, medium: 3, high: 4, unknown: 0 };

/** Drawn for a screen reader only - see `MeasureList`. The bars are the visible level. */
const LEVEL_WORDS: Record<string, string> = {
  none: "none",
  low: "low",
  medium: "medium",
  high: "high",
  unknown: "not established",
};

const MEASURE_LABELS: Record<string, string> = {
  intent: "How clearly they said what they want",
  urgency: "How soon",
  fit: "Something you do",
  reach: "A way to reach them",
  momentum: "Something agreed",
};

/**
 * All five, in one list and in this order.
 *
 * They used to be split - three under the meter, two under a "What the call came away with"
 * rule - and the split cost a whole sub-heading and a caption to explain a distinction the
 * notes beside each measure already make. The order still reads enquiry-then-capture, so
 * nothing about how it scans has changed; there is simply one list where there were two.
 */
const MEASURES = ["intent", "urgency", "fit", "reach", "momentum"];

/**
 * The bars. `aria-hidden` on the segments is paid for by the visually-hidden level word
 * every call site puts beside them - the same handling `Heatmap` gives its cells.
 */
function Steps({
  lit,
  of = 4,
  fill,
  dashed,
}: {
  lit: number;
  of?: number;
  fill: string;
  dashed?: boolean;
}) {
  return (
    <span aria-hidden="true" className="flex gap-0.5">
      {Array.from({ length: of }, (_, i) => (
        <span
          key={i}
          className={cx(
            "h-2 flex-1 rounded-full",
            i < lit ? fill : dashed ? "border border-dashed border-line-strong" : "bg-line-soft",
          )}
        />
      ))}
    </span>
  );
}

/**
 * Which quarter a score sits in, which is the same arithmetic the band cutoffs use.
 *
 * 0-24 lights one, 25-49 two, 50-74 three, 75-100 four - so the bars and the band word
 * are two readings of one number rather than two numbers. The one case where they appear
 * to disagree is the urgency cap: four lit steps under the word "Cool" is a call with no
 * date on it, and the panel below prints that sentence rather than leaving it mysterious.
 */
function litFor(score: number): number {
  return Math.max(1, Math.min(4, Math.floor(score / 25) + 1));
}

/**
 * Whether the band was capped by the no-date rule.
 *
 * Derived from the two things already on the screen - the score and the urgency bar -
 * rather than carried as a fourth column. That is the point of equal weights and quarter
 * cutoffs: the arithmetic is reproducible by the reader, so it is reproducible here.
 */
function wasCapped(qualification: Qualification): boolean {
  const urgency = qualification.dimensions?.find((m) => m.dimension === "urgency");
  return urgency?.level === "none" && (qualification.score ?? 0) >= 50;
}

/**
 * The log's cell: the meter and the band word on ONE line.
 *
 * IT IS THE ONLY CELL FORM LEFT. There used to be a `QualityCell` beside it - a taller,
 * stacked block with the capture sentence underneath - built for a table column that no
 * longer exists. It sat exported and imported by nothing for long enough that its own
 * doc-comment was arguing with a layout nobody could see, so it has gone. What it shared
 * with this - `Steps` and `litFor` - is what was worth keeping, and the detail panel below
 * is the other reader.
 *
 * `Steps` sizes its segments with `flex-1`, so it needs a parent with a width. 36px is
 * about the width of the band word beside it and reads as a mark rather than as a chart.
 *
 * NO CAPTURE FLAG. A red "no capture" used to ride the end of this line, and three words
 * in the smallest type on the page could not say what had not been captured - the fact it
 * carries is "we came away with no way to reach this person", which needs the sentence
 * `QualityCell` and `QualificationDetail` have room for and this line does not.
 */
export function QualityInline({
  qualification,
  labels,
}: {
  qualification: Qualification | null;
  labels: Record<string, string>;
}) {
  // A call read before this existed, or one nobody has read at all. NO BAR, and the words
  // in the faintest ink there is: cold always lights a step and cold's word is `text-ink`
  // at font-medium, so the two can never be mistaken for each other.
  if (!qualification) return <span className="text-sm text-ink-faint">Not scored</span>;

  const { band, score } = qualification;
  const word = bandWord(labels, band);
  // The gates and the floor have no score, so they have no meter either - a bar drawn at
  // zero for a wrong number is a measurement of something nobody measured.
  if (score === null) return <span className="text-sm text-ink-soft">{word}</span>;

  const lit = litFor(score);
  return (
    <span className="flex items-center gap-2">
      {/* A BLOCK, NOT A FLEX ITEM. `Steps` divides its parent's width between four
          `flex-1` segments, and a flex child inside a flex row has no width to divide -
          the bars collapsed to nothing and the cell looked like it had no meter at all. */}
      <span className="block w-9 flex-none">
        <Steps lit={lit} fill={HEAT[lit - 1]!} />
      </span>
      <span className="text-sm font-medium text-ink">
        {word}
        <span className="sr-only"> — {score} out of 100</span>
      </span>
    </span>
  );
}

/**
 * The score as a ring, and the five measures that justify it as a list under it.
 *
 * IT DRAWS A CARD NOW, AND THAT REVERSES WHAT THIS COMMENT USED TO SAY. It was
 * `QualificationPanel`, then it was deliberately not a panel at all: "a card here would be
 * a box inside a box inside a box", written when the reading column was 440px wide and
 * every section in it was a heading over a paragraph. The column is 576px now, this block
 * is the one thing in it with internal structure - a figure, then five rows that are the
 * evidence for the figure - and a hairline is not enough to say where that structure
 * starts and stops. One card, and the five sections around it stay hairlines.
 *
 * THE NUMBER AND THE EVIDENCE, SIDE BY SIDE. The score used to sit on a line of its own
 * above the measures, which put the one thing worth reading and the five things that
 * justify it two hundred pixels apart. Beside each other they are one statement: this
 * grade, because of these five.
 *
 * IT MEASURES ITS CONTAINER, NOT THE WINDOW. The breakpoints below used to be
 * `max-[720px]`, which is wrong the moment this sits in a 576px column on a 1440px screen:
 * the window says "wide", the three-track measure row fires, and the quote column - the
 * half anybody actually reads - squashes to nothing. `Fields.tsx` hit the identical bug and
 * its comment argues the identical fix. With no `@container` ancestor a `@max-*` variant
 * never matches, so a future full-width caller gets the wide layout by default.
 *
 * THE 560px BREAKPOINT IS 24 PIXELS FROM WHAT THE DIALOG ACTUALLY GIVES THIS. A 1440px
 * dialog's reading column is 576px, and its content box after padding is about 536 - so
 * this block is on the STACKED side of its own query in the layout it was designed for,
 * and the wide branch is for a future caller with real room. Change the dialog's padding
 * and the two swap over, which is worth knowing before it happens by accident.
 *
 * Returns null for `unscorable`, because the `Nothing` block already says it in a sentence
 * and saying it twice in different words reads as two different facts.
 */
/**
 * Is there a grade to draw at all?
 *
 * Exported because the caller has to know BEFORE it puts a heading over this. A component
 * that returns null still leaves whatever framed it on screen, and a section reading
 * "Enquiry strength" over nothing is the empty-panel failure the whole call layout is
 * arranged to avoid. One predicate, read in both places, so they cannot disagree.
 */
export function hasQualification(qualification: Qualification | null): boolean {
  return Boolean(qualification && qualification.band !== "unscorable");
}

/**
 * What the score means in a sentence, which is the half of it a number cannot say.
 *
 * Cut at the same quarters `litFor` uses, so the ring, the bars and this agree by
 * construction rather than by somebody remembering to move three numbers together.
 */
function scoreNote(score: number): string {
  if (score >= 75) return "Every part of the enquiry is in place. Worth following up today.";
  if (score >= 50) return "Most of the enquiry is in place. One call should close the gaps.";
  if (score >= 25) return "Some of the enquiry is in place. A follow-up should fill the rest.";
  return "Most of what makes an enquiry actionable was never established on this call.";
}

export function QualificationDetail({
  qualification,
  labels,
}: {
  qualification: Qualification | null;
  labels: Record<string, string>;
}) {
  if (!hasQualification(qualification) || !qualification) return null;

  const { band, score, capture } = qualification;
  const dimensions = qualification.dimensions ?? [];
  const find = (dimension: string) => dimensions.find((m) => m.dimension === dimension);

  // A gate fired. ONE SENTENCE SAYING WHICH AND WHY, not five empty bars: the gates decide
  // whether there IS a score rather than lowering one, and drawing a meter under either of
  // them would present a decision not to measure as a measurement.
  if (score === null) {
    return (
      <p className="text-base text-ink-soft">
        <span className="font-medium text-ink">{bandWord(labels, band)}.</span>{" "}
        {band === "not_an_enquiry"
          ? "This call was not a piece of business — an existing customer, a wrong number, a supplier or something like it — so it has no lead score."
          : "The call showed this is something you don’t take on, so it has no lead score. If that is wrong, the description on your settings page is what this was judged against."}
      </p>
    );
  }

  // A call read before the five measures existed - and every fixture, which carries a band
  // and a score and nothing else. There is a grade and nothing to put beside it, so the
  // card does NOT split: a narrow ring against half a card of white reads as a column that
  // failed to load rather than as a call nobody measured.
  const measured = MEASURES.some((dimension) => find(dimension));
  const lit = litFor(score);

  return (
    <div className="overflow-hidden rounded-lg border border-line">
      <div
        className={cx(
          "flex items-center gap-5 bg-sunken px-5 py-4",
          measured && "border-b border-line",
        )}
      >
        <ScoreRing score={score} label={`Enquiry strength ${score} out of 100`} />
        <div className="grid min-w-0 gap-1.5">
          <span className="flex flex-wrap items-center gap-2.5">
            <span className="text-xl font-semibold tracking-[-0.02em] text-accent">
              {bandWord(labels, band)}
            </span>
            {/* THE SAME METER THE LOG DRAWS, and that is why it is here rather than a
                second reading of the same number in some other shape. A reader who
                recognised this row in the list should recognise it again on the call. */}
            <span className="block w-9 flex-none">
              <Steps lit={lit} fill={HEAT[lit - 1]!} />
            </span>
          </span>
          <span className="text-sm leading-snug text-ink-soft">{scoreNote(score)}</span>
          {capture !== "good" && capture !== null && (
            // The fact the old "What the call came away with" rule carried, kept without
            // the rule: a hot lead with no way back to them is the row this feature exists
            // for, and it must not go missing because a heading did.
            <span className="text-xs text-ink-soft">{CAPTURE_WORDS[capture]}</span>
          )}
        </div>
      </div>

      {measured && <MeasureList dimensions={MEASURES.map(find)} />}

      {wasCapped(qualification) && (
        // Printed rather than left to be discovered. Without this the reader sees a
        // score in the seventies and the word "Cool", and has no way to tell whether
        // the page is broken.
        <p className="border-t border-line-soft px-5 py-3 text-sm text-ink-soft">
          Held at {bandWord(labels, "cool").toLowerCase()} because nobody named a date. The score
          is unchanged — a callback booked against someone with no timeframe wastes the callback.
        </p>
      )}
    </div>
  );
}

/**
 * What each measure is about, as a glyph.
 *
 * ALL FIVE ARE ALREADY IN THE SET, which is the reason this is a lookup and not five new
 * drawings. `Icons.tsx` is deliberately one stroke weight on one grid, and a sixth-hand
 * glyph traced for one panel is exactly how that stops being true.
 */
const MEASURE_ICONS: Record<string, ReactNode> = {
  intent: <Sparkle size={14} />,
  urgency: <Clock size={14} />,
  fit: <Stack size={14} />,
  reach: <Phone size={14} />,
  momentum: <Check size={14} />,
};

/** What the bars add up to, in a word. `unknown` is not a low score and never reads as one. */
const LEVEL_STATE: Record<string, string> = {
  none: "Not established",
  low: "Partial",
  medium: "Partial",
  high: "Established",
  unknown: "Not asked",
};

function MeasureList({ dimensions }: { dimensions: Array<Measure | undefined> }) {
  return (
    <ul className="grid list-none gap-0 p-0">
      {dimensions.filter((m): m is Measure => !!m).map((measure, index) => {
        const steps = LEVEL_STEPS[measure.level] ?? 0;
        const full = measure.level === "high";
        const asked = measure.level !== "unknown";
        return (
          <li
            key={measure.dimension}
            className={cx(
              "grid grid-cols-[28px_minmax(0,1fr)_92px] items-start gap-x-4 gap-y-2 px-5 py-3",
              // Nobody asked, so the whole row steps back rather than only its bars. A
              // measure at full contrast with an empty meter reads as a bad answer.
              !asked && "bg-ground",
              index > 0 && "border-t border-line-soft",
              /*
               * 440, NOT 560, AND THE NUMBER WAS MEASURED RATHER THAN GUESSED. The call
               * dialog's reading column is 576px and its content box is about 536, so a
               * 560px breakpoint fired the stacked layout in the ONE place this component
               * is used - the bars ended up under the note, left-aligned, at the label's x,
               * which is the arrangement this row exists to avoid. There is easily room for
               * a 92px meter column at 536: the note still gets about 344.
               *
               * Below 440 the meter genuinely does not fit beside a readable note, and it
               * drops under the text rather than beside the icon - which is what the
               * `col-start-2` on it is for.
               */
              "@max-[440px]:grid-cols-[28px_minmax(0,1fr)]",
            )}
          >
            <span
              aria-hidden="true"
              className={cx(
                "grid size-7 place-items-center rounded-xs",
                full
                  ? "bg-accent text-accent-ink"
                  : asked
                    ? "bg-accent-soft text-accent"
                    : "bg-sunken text-ink-ghost",
              )}
            >
              {MEASURE_ICONS[measure.dimension]}
            </span>

            <div className="grid min-w-0 gap-0.5">
              <span className={cx("text-sm font-semibold", asked ? "text-ink" : "text-ink-soft")}>
                {MEASURE_LABELS[measure.dimension] ?? measure.dimension}
              </span>
              {/* The level is what you count, the note is what you actually read. A level
                  with no quote under it is a grade nobody can check. */}
              <span className="text-sm leading-snug text-ink-soft">
                {measure.note ?? <span className="text-ink-ghost">—</span>}
              </span>
            </div>

            <div className="grid justify-items-end gap-1 pt-1 @max-[440px]:col-start-2 @max-[440px]:justify-items-start">
              <span className="block w-full max-w-[76px]">
                <Steps
                  lit={steps}
                  fill={HEAT[Math.max(0, steps - 1)]!}
                  dashed={measure.level === "unknown"}
                />
              </span>
              {/*
                THE STATE WORD IS BACK, AND IT IS NOT THE LEVEL WORD THAT WAS REMOVED.
                What used to sit here was "high" / "low" printed under the bars - one fact
                said twice, in a column of grey captions between the measure and the
                sentence that justifies it. This says something the bars cannot: four lit
                and four dashed are both "four segments" until you look closely, and
                "Established" versus "Not asked" is the distinction somebody is scanning
                for. The `sr-only` level is unchanged, because a screen reader gets no bars.
              */}
              <span
                className={cx(
                  "text-2xs font-semibold tracking-[0.04em] uppercase",
                  full ? "text-accent" : asked ? "text-ink-faint" : "text-ink-ghost",
                )}
              >
                {LEVEL_STATE[measure.level] ?? measure.level}
                <span className="sr-only"> — {LEVEL_WORDS[measure.level] ?? measure.level}</span>
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
